import type { TriageAction, TriageCandidate } from "@/lib/api";
import {
  ACTION_KEY,
  ACTION_LABEL,
  type StageInput,
  type Staged,
  allowedActions,
  needsPanel,
} from "@/lib/triage";
import { Box, Text } from "@okshaun/components";
import { css } from "@styled-system/css";
import { useEffect, useRef, useState } from "react";
import { DateChoice, fieldClass } from "./date-choice";

const ORDER: TriageAction[] = ["done", "drop", "keep", "snooze", "reschedule", "note"];

const actionButton = css({
  display: "inline-flex",
  alignItems: "baseline",
  gap: "6",
  fontFamily: "mono",
  fontSize: "12",
  color: "text",
  bg: "transparent",
  border: "default",
  borderRadius: "4",
  px: "10",
  py: "6",
  cursor: "pointer",
  outline: "none",
  transitionProperty: "[color, background-color, border-color]",
  transitionDuration: "fast",
  _hover: { bg: "surface.hovered", borderColor: "border.bold" },
  _active: { bg: "surface.pressed" },
  _focusVisible: { boxShadow: "[0 0 0 2px var(--oks-colors-border-focused)]" },
  _disabled: {
    color: "text.disabled",
    cursor: "not-allowed",
    _hover: { bg: "transparent", borderColor: "border" },
  },
  "&[aria-pressed=true]": {
    color: "text.selected",
    bg: "bg.selected",
    borderColor: "border.selected",
  },
});

const keyHint = css({ color: "text.subtlest", fontSize: "[10px]" });

const noteField = css({
  fontFamily: "sans",
  fontSize: "[13px]",
  color: "text",
  bg: "bg.input",
  border: "default",
  borderColor: "border.input",
  borderRadius: "4",
  px: "8",
  py: "6",
  minH: "[64px]",
  outline: "none",
  resize: "vertical",
  _focusVisible: { borderColor: "border.focused" },
});

const panel = css({
  display: "flex",
  flexDir: "column",
  gap: "10",
  p: "12",
  border: "default",
  borderRadius: "4",
});

const checkboxRow = css({
  display: "flex",
  gap: "8",
  alignItems: "center",
  fontSize: "[13px]",
  color: "text",
});

const primary = css({
  fontFamily: "mono",
  fontSize: "12",
  color: "text.inverse",
  bg: "bg.brand.bold",
  border: "none",
  borderRadius: "4",
  px: "12",
  py: "6",
  cursor: "pointer",
  outline: "none",
  _hover: { bg: "bg.brand.bold.hovered" },
  _active: { bg: "bg.brand.bold.pressed" },
  _focusVisible: { boxShadow: "[0 0 0 2px var(--oks-colors-border-focused)]" },
});

const quiet = css({
  fontFamily: "mono",
  fontSize: "12",
  color: "text.subtle",
  bg: "transparent",
  border: "none",
  px: "6",
  py: "6",
  cursor: "pointer",
  outline: "none",
  _hover: { color: "text" },
  _focusVisible: { textDecoration: "underline" },
});

const WHY_DISABLED: Partial<Record<TriageAction, string>> = {
  snooze: "This task has a deadline. Reschedule it instead.",
  reschedule: "Only a task with one deadline can be rescheduled.",
};

interface ActionBarProps {
  card: TriageCandidate;
  today: string;
  staged?: Staged;
  /** The open panel, if any. Controlled so the page's keyboard shortcuts can open it. */
  mode: TriageAction | null;
  setMode: (m: TriageAction | null) => void;
  /** Returns an error message, or null when staged. */
  onStage: (input: StageInput) => string | null;
  onUnstage: () => void;
}

/**
 * Done / Drop / Keep stage at once. Snooze, Reschedule and Note open a small panel.
 * Done or Drop on a task with open sub-tasks also opens it, to ask about the sub-tasks.
 */
export function ActionBar({
  card,
  today,
  staged,
  mode,
  setMode,
  onStage,
  onUnstage,
}: ActionBarProps) {
  const allowed = allowedActions(card);
  const [date, setDate] = useState(staged?.date ?? "");
  const [reason, setReason] = useState(staged?.reason ?? "");
  const [note, setNote] = useState(staged?.note ?? "");
  const [withChildren, setWithChildren] = useState(staged?.includeChildren ?? true);
  const [error, setError] = useState<string | null>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  // "n" (or the Note button) opens the panel to write: put the cursor in the note.
  useEffect(() => {
    if (mode === "note") noteRef.current?.focus();
  }, [mode]);

  const click = (a: TriageAction) => {
    setError(null);
    if (!allowed[a]) return;
    if (needsPanel(card, a)) {
      setMode(mode === a ? null : a);
      return;
    }
    setError(onStage({ action: a, note: note || undefined }));
  };

  const submit = () => {
    if (!mode) return;
    // A note on its own attaches to the decision already staged, if there is one.
    const action = mode === "note" && staged && staged.action !== "note" ? staged.action : mode;
    const input: StageInput = {
      action,
      date:
        action === "snooze" || action === "reschedule"
          ? mode === "note"
            ? staged?.date
            : date
          : undefined,
      reason:
        action === "reschedule" ? (mode === "note" ? staged?.reason : reason) : reason || undefined,
      note: note || undefined,
      includeChildren:
        action === "done" || action === "drop"
          ? mode === "note"
            ? staged?.includeChildren
            : withChildren
          : undefined,
    };
    const err = onStage(input);
    setError(err);
    if (!err) setMode(null);
  };

  return (
    <Box display="flex" flexDir="column" gap="10">
      <Box display="flex" gap="6" flexWrap="wrap" role="toolbar" aria-label="Decide this task">
        {ORDER.map((a) => (
          <button
            key={a}
            type="button"
            className={actionButton}
            disabled={!allowed[a]}
            title={allowed[a] ? `${ACTION_LABEL[a]} (${ACTION_KEY[a]})` : WHY_DISABLED[a]}
            aria-pressed={staged?.action === a || mode === a}
            aria-keyshortcuts={ACTION_KEY[a]}
            onClick={() => click(a)}
          >
            {ACTION_LABEL[a]}
            <span className={keyHint}>{ACTION_KEY[a]}</span>
          </button>
        ))}
        {staged && (
          <button type="button" className={quiet} onClick={onUnstage}>
            unstage
          </button>
        )}
      </Box>

      {!allowed.snooze && (
        <Text textStyle="mono.xs" color="text.subtlest">
          Snooze is off: this task has a real deadline. Reschedule it, with a reason.
        </Text>
      )}

      {mode && (
        <form
          className={panel}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {(mode === "done" || mode === "drop") && card.children.length > 0 && (
            <label className={checkboxRow}>
              <input
                type="checkbox"
                checked={withChildren}
                onChange={(e) => setWithChildren(e.target.checked)}
              />
              Also {mode === "done" ? "mark done" : "drop"} its {card.children.length} open sub-task
              {card.children.length === 1 ? "" : "s"}
            </label>
          )}
          {mode === "snooze" && (
            <Box display="flex" flexDir="column" gap="6">
              <Text textStyle="mono.xs" color="text.subtle">
                Hide it until a date. Hal adds #remind 📅, and bee posts it to #hal that morning.
              </Text>
              <DateChoice today={today} value={date} onChange={setDate} />
            </Box>
          )}
          {mode === "reschedule" && (
            <Box display="flex" flexDir="column" gap="6">
              <Text textStyle="mono.xs" color="text.subtle">
                Move the real deadline. The note keeps the reason next to the date.
              </Text>
              <DateChoice today={today} value={date} onChange={setDate} from={card.due} />
              <input
                className={fieldClass}
                aria-label="Reason"
                placeholder="Why it moved (required), e.g. Taylor moved the review"
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
              />
            </Box>
          )}
          <textarea
            ref={noteRef}
            className={noteField}
            aria-label="Note to Hal"
            placeholder={
              mode === "note"
                ? "Note to Hal (stored, Hal reads it in a session)"
                : "Optional note to Hal"
            }
            value={note}
            maxLength={2000}
            onChange={(e) => setNote(e.target.value)}
          />
          <Box display="flex" gap="8" alignItems="center">
            <button type="submit" className={primary}>
              Stage{" "}
              {
                ACTION_LABEL[
                  mode === "note" && staged && staged.action !== "note" ? staged.action : mode
                ]
              }
            </button>
            <button type="button" className={quiet} onClick={() => setMode(null)}>
              cancel (esc)
            </button>
          </Box>
        </form>
      )}

      {error && (
        <Text textStyle="mono.xs" color="text.danger" role="alert">
          {error}
        </Text>
      )}
    </Box>
  );
}
